/**
 * The typed `SdkContext` handed to modules — read access to kernel state plus
 * the track service, event bus, and one-shot signals.
 */

import type { PoolWebsitePayload, SdkConfig, AdAccountConfig } from './config/types';
import type { ResolvedOptions } from './options';
import type { ResolvedIds } from './identity';
import type { ConsentStatus } from './consent/engine';
import type { PageParams } from './analytics/pageParams';
import { track, type EventParams } from './analytics/track';
import {
  emit,
  on,
  type EmitArgs,
  type EventPayloads,
  type SdkEvent,
  type Unsubscribe,
} from './events/bus';
import {
  AwaitFor,
  awaitFor,
  completeAwait,
  isAwaitCompleted,
} from './events/awaiter';
import { getSdkState, currentConsent, currentIdentity, type SdkState } from './state';

export interface SdkContext {
  readonly version: string;
  getConfig(): SdkConfig;
  getOptions(): ResolvedOptions;
  /** Attribution URL params snapshot (resolved once at init). */
  getPageParams(): PageParams;
  /** Active ad account from sourceMedium (init or page UTMs), or null. */
  getActiveAdAccount(): AdAccountConfig | null;
  /**
   * Raw pool website payload from the init fetch — pool module normalizes its
   * slice, no re-fetch. `null` in sealed builds.
   */
  getWebsitePayload(): PoolWebsitePayload | null;
  getIdentity(): ResolvedIds;
  getConsent(): ConsentStatus;
  /** Send a `/dye` analytics event. */
  track(type: string, params?: EventParams): void;
  on<K extends SdkEvent>(event: K, listener: (payload: EventPayloads[K]) => void): Unsubscribe;
  emit<K extends SdkEvent>(event: K, ...args: EmitArgs<K>): void;
  awaitFor(signal: AwaitFor): Promise<unknown>;
  completeAwait(signal: AwaitFor, value?: unknown): void;
  isAwaitCompleted(signal: AwaitFor): boolean;
  /** Pool: mark content fetch in flight (used by ecom `init` wait). */
  beginContentFetch(): void;
  endContentFetch(value?: unknown): void;
  contentFetchInFlight(): number;
  /**
   * Pool: report the locale of the served content (`post.locale`). Feeds the
   * `lo=` analytics param — the post payload is authoritative because the pool
   * may serve a fallback locale different from the one requested.
   */
  setContentLocale(locale: string | undefined): void;
}

export function createSdkContext(sdkVersion: string): SdkContext {
  const state: SdkState = getSdkState();

  return {
    version: sdkVersion,
    getConfig: () => state.config,
    getOptions: () => state.options,
    getPageParams: () => state.pageParams,
    getActiveAdAccount: () => state.activeAdAccount,
    getWebsitePayload: () => state.websitePayload,
    getIdentity: () => currentIdentity(),
    getConsent: () => currentConsent(),
    track,
    on,
    emit,
    awaitFor(signal) {
      return new Promise<unknown>((resolve) => {
        awaitFor(signal, resolve);
      });
    },
    completeAwait(signal, value) {
      completeAwait(signal, value);
    },
    isAwaitCompleted(signal) {
      return isAwaitCompleted(signal);
    },
    beginContentFetch() {
      state.contentFetch.inFlight += 1;
    },
    endContentFetch(value) {
      if (value !== undefined) {
        state.contentFetch.lastValue = value;
      }
      state.contentFetch.inFlight = Math.max(0, state.contentFetch.inFlight - 1);
      if (state.contentFetch.inFlight === 0) {
        completeAwait(AwaitFor.CONTENT, state.contentFetch.lastValue);
      }
    },
    contentFetchInFlight() {
      return state.contentFetch.inFlight;
    },
    setContentLocale(locale) {
      const normalized = (locale ?? '').trim().toLowerCase();
      if (normalized) state.content.locale = normalized;
    },
  };
}
